import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { authAxios, QueryStatus } from "../../../utils";
import {
  DriversAPI,
  DriversListType,
  UpdateDriverStatusType,
  PendingDriverAccessRequest,
  UpdateDriverAccessRequest,
  DriverStatusType,
  UpdateDriverAnalyticCode1Type,
  UpdateDriverAnalyticCode2Type,
} from "./driversAPI";
import { RootState } from "../../../redux/store";
import { capitalize } from "@qivia/ui/src/format";
import { drivingEventsCountsByDrivers, totalScoreByDrivers } from "./lib";
import { Trips } from "../fleet/trips/tripsApi";

export interface DriversState {
  driversListStatus: QueryStatus;
  driversList: DriversListType[];
  driverUpdatedStatus: QueryStatus;
  driverUpdatedAnalyticCode1Status: QueryStatus;
  driverUpdatedAnalyticCode2Status: QueryStatus;
  driverAccessRequestsListStatus: QueryStatus;
  driverAccessRequestsList: PendingDriverAccessRequest[];
  currentDriverAccessRequest: PendingDriverAccessRequest | null;
  driverNewStatus: DriverStatusType | null;
}

const initialState: DriversState = {
  driversListStatus: "idle",
  driversList: [],
  driverUpdatedStatus: "idle",
  driverUpdatedAnalyticCode1Status: "idle",
  driverUpdatedAnalyticCode2Status: "idle",
  driverAccessRequestsListStatus: "idle",
  driverAccessRequestsList: [],
  currentDriverAccessRequest: null,
  driverNewStatus: null,
};

export const driversListAsync = createAsyncThunk(
  "driversList/call",
  async (companyUuid: string, thunkAPI) => {
    const company = (thunkAPI.getState() as RootState).home.company;
    const axios = authAxios({ companyUuid: company?.uuid });
    const response = await axios.get<Array<DriversAPI>>(
      `drivers/company/${companyUuid}`,
      { timeout: 300000 },
    );

    const driversList = response.data.map((payload) => ({
      ...payload,
      cardUuid: payload.cardUuid ?? null,
      vehicleUuid: payload.vehicleUuid ?? null,
      date: payload.createdAt,
      name: capitalize(payload.firstName) + " " + capitalize(payload.lastName),
      vehicleReference: payload.vehicleReference?.toLocaleUpperCase() ?? null,
      averageMonthlyAmount: payload.averageMonthlyAmount
        ? Number((payload.averageMonthlyAmount / 100).toFixed(2))
        : null,
      transactionsWithoutSupportingDocumentCount:
        payload.missingDataTransactionCount
          ?.transactionsWithoutSupportingDocumentCount ?? null,
      transactionsWithoutMileageCount:
        payload.missingDataTransactionCount?.transactionsWithoutMileageCount ??
        null,
      averageQuantity: payload.averageQuantity
        ? Number(payload.averageQuantity.toFixed(2))
        : null,
      ecoDrivingScore: null,
      drivingEvents: null,
    }));
    return driversList;
  },
);

export const driversOfADepartmentListAsync = createAsyncThunk(
  "driversForADepartment/listDriversByDepartment",
  async (departmentUuid: string, thunkAPI) => {
    const department = (thunkAPI.getState() as RootState)
      .departmentsForDepartmentUser.currentDepartmentUuid;
    if (!department) {
      throw new Error("Department not selected");
    }

    const axios = authAxios({ departmentUuid: department });
    const response = await axios.get<Array<DriversAPI>>(
      `departments/drivers/department/${departmentUuid}`,
      { timeout: 300000 },
    );

    const driversList = response.data.map((payload) => ({
      ...payload,
      cardUuid: payload.cardUuid ?? null,
      vehicleUuid: payload.vehicleUuid ?? null,
      date: payload.createdAt,
      name: capitalize(payload.firstName) + " " + capitalize(payload.lastName),
      vehicleReference: payload.vehicleReference?.toLocaleUpperCase() ?? null,
      averageMonthlyAmount: payload.averageMonthlyAmount
        ? Number((payload.averageMonthlyAmount / 100).toFixed(2))
        : null,
      transactionsWithoutSupportingDocumentCount:
        payload.missingDataTransactionCount
          ?.transactionsWithoutSupportingDocumentCount ?? null,
      transactionsWithoutMileageCount:
        payload.missingDataTransactionCount?.transactionsWithoutMileageCount ??
        null,
      averageQuantity: payload.averageQuantity
        ? Number(payload.averageQuantity.toFixed(2))
        : null,
      ecoDrivingScore: null,
      drivingEvents: null,
    }));
    return driversList;
  },
);

export const driversAccessRequestsListAsync = createAsyncThunk(
  "driversAccessRequestsList/call",
  async (companyUuid: string, thunkAPI) => {
    const company = (thunkAPI.getState() as RootState).home.company;
    const axios = authAxios({ companyUuid: company?.uuid });
    const response = await axios.get<Array<PendingDriverAccessRequest>>(
      `pending_access_requests/company/${companyUuid}`,
    );
    return response.data;
  },
);

export const driversAccessRequestsListByDepartmentAsync = createAsyncThunk(
  "driversAccessRequestsListByDepartmentAsync/call",
  async (departmentUuid: string) => {
    const axios = authAxios({ departmentUuid });
    const response = await axios.get<Array<PendingDriverAccessRequest>>(
      `departments/drivers/pending_access_requests/department/${departmentUuid}`,
    );
    return response.data;
  },
);

export const updateDriverAccessRequestAsync = createAsyncThunk(
  "updateDriverAccessRequest/call",
  async (payload: UpdateDriverAccessRequest, thunkAPI) => {
    const company = (thunkAPI.getState() as RootState).home.company;
    if (!company) {
      throw new Error("Company not selected");
    }
    const axios = authAxios({ companyUuid: company?.uuid });
    await axios.put(
      `pending_access_request/${payload.uuid}/${payload.status}/company/${company?.uuid}`,
    );
    void thunkAPI.dispatch(driversAccessRequestsListAsync(company?.uuid));
  },
);

export const updateDriverAccessRequestByDepartmentAsync = createAsyncThunk(
  "updateDriverAccessRequestByDepartmentAsync/call",
  async (payload: UpdateDriverAccessRequest, thunkAPI) => {
    const departmentUuid = (thunkAPI.getState() as RootState)
      .departmentsForDepartmentUser.currentDepartmentUuid;
    if (!departmentUuid) {
      throw new Error("Department not selected");
    }
    const axios = authAxios({ departmentUuid });
    await axios.put(
      `departments/drivers/pending_access_request/${payload.uuid}/${payload.status}/department/${departmentUuid}`,
    );
    void thunkAPI.dispatch(
      driversAccessRequestsListByDepartmentAsync(departmentUuid),
    );
  },
);

export const updateDriverStatusAsync = createAsyncThunk(
  "updateDriverStatus/call",
  async (payload: UpdateDriverStatusType, thunkAPI) => {
    const company = (thunkAPI.getState() as RootState).home.company;
    const axios = authAxios({ companyUuid: company?.uuid });
    await axios.put(`/driver/${payload.driverUuid}/update_status`, {
      status: payload.status,
      companyUuid: payload.companyUuid,
    });
    return payload.status;
  },
);

export const updateDriverAnalyticCode1Async = createAsyncThunk(
  "updateDriverAnalyticCode1/call",
  async (payload: UpdateDriverAnalyticCode1Type, thunkAPI) => {
    const company = (thunkAPI.getState() as RootState).home.company;
    const axios = authAxios({ companyUuid: company?.uuid });
    await axios.put(`/driver/${payload.driverUuid}/update_analytic_code_1`, {
      analyticCode1: payload.analyticCode1,
      companyUuid: payload.companyUuid,
    });
  },
);

export const updateDriverAnalyticCode2Async = createAsyncThunk(
  "updateDriverAnalyticCode/call",
  async (payload: UpdateDriverAnalyticCode2Type, thunkAPI) => {
    const company = (thunkAPI.getState() as RootState).home.company;
    const axios = authAxios({ companyUuid: company?.uuid });
    await axios.put(`/driver/${payload.driverUuid}/update_analytic_code_2`, {
      analyticCode2: payload.analyticCode2,
      companyUuid: payload.companyUuid,
    });
  },
);

export const driversSlice = createSlice({
  name: "drivers",
  initialState,
  // The `reducers` field lets us define reducers and generate associated actions
  reducers: {
    getEcoDrivingScoreAndDrivingEventsByDriver(
      state,
      { payload }: PayloadAction<Trips[]>,
    ) {
      const driverEcoScores = payload ? totalScoreByDrivers(payload) : null;
      if (!driverEcoScores) return;
      const drivingEvents = payload
        ? drivingEventsCountsByDrivers(payload)
        : null;

      state.driversList = state.driversList.map((driver) => ({
        ...driver,
        ecoDrivingScore: driverEcoScores[driver.uuid],
        drivingEvents: drivingEvents ? drivingEvents[driver.uuid] : null,
      }));
    },
    resetDriversListStatus(state) {
      state.driversListStatus = "idle";
    },
    resetDriversAccessRequestsListStatus(state) {
      state.driverAccessRequestsListStatus = "idle";
    },
    resetDriverUpdatedStatus(state) {
      state.driverUpdatedStatus = "idle";
    },
    resetDriverUpdatedAnalyticCode1Status(state) {
      state.driverUpdatedAnalyticCode1Status = "idle";
    },
    resetDriverUpdatedAnalyticCode2Status(state) {
      state.driverUpdatedAnalyticCode2Status = "idle";
    },
  },
  // The `extraReducers` field lets the slice handle actions defined elsewhere,
  // including actions generated by createAsyncThunk or in other slices.
  extraReducers: (builder) => {
    builder
      .addCase(driversListAsync.pending, (state) => {
        state.driversListStatus = "processing";
      })
      .addCase(driversListAsync.fulfilled, (state, action) => {
        state.driversList = action.payload;
        state.driversListStatus = "success";
        state.driverUpdatedStatus = "idle";
        state.driverUpdatedAnalyticCode1Status = "idle";
        state.driverUpdatedAnalyticCode2Status = "idle";
        state.driverNewStatus = null;
      })
      .addCase(driversListAsync.rejected, (state) => {
        state.driversListStatus = "failed";
      })
      .addCase(driversOfADepartmentListAsync.pending, (state) => {
        state.driversListStatus = "processing";
      })
      .addCase(driversOfADepartmentListAsync.fulfilled, (state, action) => {
        state.driversList = action.payload;
        state.driversListStatus = "success";
        state.driverUpdatedStatus = "idle";
        state.driverUpdatedAnalyticCode1Status = "idle";
        state.driverUpdatedAnalyticCode2Status = "idle";
        state.driverNewStatus = null;
      })
      .addCase(driversOfADepartmentListAsync.rejected, (state) => {
        state.driversListStatus = "failed";
      })
      .addCase(updateDriverStatusAsync.pending, (state) => {
        state.driverUpdatedStatus = "processing";
      })
      .addCase(updateDriverStatusAsync.fulfilled, (state, action) => {
        state.driverUpdatedStatus = "success";
        state.driverNewStatus = action.payload;
      })
      .addCase(updateDriverStatusAsync.rejected, (state) => {
        state.driverUpdatedStatus = "failed";
      })
      .addCase(driversAccessRequestsListAsync.pending, (state) => {
        state.driverAccessRequestsListStatus = "processing";
      })
      .addCase(driversAccessRequestsListAsync.fulfilled, (state, action) => {
        state.driverAccessRequestsList = action.payload;
        state.currentDriverAccessRequest = action.payload[0] ?? null;
        state.driverAccessRequestsListStatus = "success";
      })
      .addCase(driversAccessRequestsListAsync.rejected, (state) => {
        state.driverAccessRequestsListStatus = "failed";
      })
      .addCase(driversAccessRequestsListByDepartmentAsync.pending, (state) => {
        state.driverAccessRequestsListStatus = "processing";
      })
      .addCase(
        driversAccessRequestsListByDepartmentAsync.fulfilled,
        (state, action) => {
          state.driverAccessRequestsList = action.payload;
          state.currentDriverAccessRequest = action.payload[0] ?? null;
          state.driverAccessRequestsListStatus = "success";
        },
      )
      .addCase(driversAccessRequestsListByDepartmentAsync.rejected, (state) => {
        state.driverAccessRequestsListStatus = "failed";
      })
      .addCase(updateDriverAnalyticCode1Async.pending, (state) => {
        state.driverUpdatedAnalyticCode1Status = "processing";
      })
      .addCase(updateDriverAnalyticCode1Async.fulfilled, (state) => {
        state.driverUpdatedAnalyticCode1Status = "success";
      })
      .addCase(updateDriverAnalyticCode1Async.rejected, (state) => {
        state.driverUpdatedAnalyticCode1Status = "failed";
      })
      .addCase(updateDriverAnalyticCode2Async.pending, (state) => {
        state.driverUpdatedAnalyticCode2Status = "processing";
      })
      .addCase(updateDriverAnalyticCode2Async.fulfilled, (state) => {
        state.driverUpdatedAnalyticCode2Status = "success";
      })
      .addCase(updateDriverAnalyticCode2Async.rejected, (state) => {
        state.driverUpdatedAnalyticCode2Status = "failed";
      });
  },
});

export const selectDriversList = (state: RootState) =>
  state.drivers.driversList;

export const selectDriversListStatus = (state: RootState) =>
  state.drivers.driversListStatus;

export const selectDriverUpdatedStatus = (state: RootState) =>
  state.drivers.driverUpdatedStatus;

export const selectDriverAccessRequestsListStatus = (state: RootState) =>
  state.drivers.driverAccessRequestsListStatus;

export const selectDriverAccessRequestsList = (state: RootState) =>
  state.drivers.driverAccessRequestsList;

export const selectCurrentDriverAccessRequest = (state: RootState) =>
  state.drivers.currentDriverAccessRequest;

export const selectDriverNewStatus = (state: RootState) =>
  state.drivers.driverNewStatus;

export const selectDriverAnalyticCode1UpdatedStatus = (state: RootState) =>
  state.drivers.driverUpdatedAnalyticCode1Status;

export const selectDriverAnalyticCode2UpdatedStatus = (state: RootState) =>
  state.drivers.driverUpdatedAnalyticCode2Status;

export default driversSlice.reducer;
